<template>
  <v-dialog v-model="dialog" max-width="1600px" class="fontNotoSans400" persistent>
    <div class="yearlyModalCard">
      <div class="modal-header">
        <div class="header-left">
          <h2>비과세 적용 전후 비교표 (1년치)</h2>
          <v-icon 
            class="help-icon" 
            :color="showHelp ? '#1565c0' : '#888'" 
            @click="showHelp = !showHelp"
            title="도움말 보기"
          >mdi-help-circle-outline</v-icon>
        </div>
        <v-icon class="close-btn" color="#333" @click="closeDialog">mdi-close</v-icon>
      </div>

      <div class="help-section" v-if="showHelp">
        <div class="help-header">
          <span class="help-title">이 표는 뭔가요?</span>
          <v-icon size="18" color="#666" @click="showHelp = false" class="help-close">mdi-close</v-icon>
        </div>
        <div class="help-content">
          <div class="help-block">
            <div class="help-subtitle">한 줄 요약</div>
            <p class="help-highlight">
              "생산직 비과세"를 적용하면 기관이 내는 4대보험료와 퇴직금이 줄어듭니다.<br>
              이 표는 <strong>적용 전</strong>과 <strong>적용 후</strong>를 비교해서 <strong>얼마나 절감되는지</strong> 보여줍니다.
            </p>
          </div>

          <div class="help-block">
            <div class="help-subtitle">개선전 vs 개선후가 뭐예요?</div>
            <div class="help-comparison">
              <div class="help-compare-box before">
                <strong>개선전</strong>
                <p>기존 비과세만 적용<br>(식대, 차량유지비 등)</p>
              </div>
              <div class="help-arrow">→</div>
              <div class="help-compare-box after">
                <strong>개선후</strong>
                <p>생산직 비과세 추가 적용<br>(연장근무, 야간근무 등)</p>
              </div>
            </div>
          </div>

          <div class="help-block">
            <div class="help-subtitle">각 항목 설명</div>
            <table class="help-table">
              <tr>
                <td class="help-term">비과세</td>
                <td>세금을 안 내는 급여. 비과세가 많으면 → 과세소득이 줄어듦 → 4대보험료 감소</td>
              </tr>
              <tr>
                <td class="help-term">기관부담 4대보험</td>
                <td>기관이 내는 국민연금, 건강보험, 고용보험 등의 합계</td>
              </tr>
              <tr>
                <td class="help-term">퇴직금</td>
                <td>퇴직금 산정 기초 = (총급여 - 식대) ÷ 12</td>
              </tr>
              <tr>
                <td class="help-term">차액</td>
                <td>개선후 - 개선전. <span class="text-green">음수(-)면 절감</span>, <span class="text-red">양수(+)면 증가</span></td>
              </tr>
              <tr>
                <td class="help-term">월 절감액</td>
                <td>기관부담 차액 + 퇴직금 차액. <span class="text-green">음수(-)가 좋은 것!</span></td>
              </tr>
            </table>
          </div>

          <div class="help-block">
            <div class="help-subtitle">계산 예시</div>
            <div class="help-example">
              <p>직원 A씨의 총급여: <strong>3,000,000원</strong></p>
              <div class="help-calc-row">
                <span class="calc-label">개선전 비과세:</span>
                <span>식대 20만 + 차량 20만 = <strong>40만원</strong></span>
              </div>
              <div class="help-calc-row">
                <span class="calc-label">개선전 과세소득:</span>
                <span>300만 - 40만 = <strong>260만원</strong></span>
              </div>
              <div class="help-calc-row mt">
                <span class="calc-label">개선후 비과세:</span>
                <span>40만 + 연장야간 24만 = <strong>64만원</strong></span>
              </div>
              <div class="help-calc-row">
                <span class="calc-label">개선후 과세소득:</span>
                <span>300만 - 64만 = <strong>236만원</strong></span>
              </div>
              <div class="help-calc-row result">
                <span class="calc-label">결과:</span>
                <span>과세소득 24만원 감소 → 4대보험료 약 2~3만원 절감</span>
              </div>
            </div>
          </div>

          <div class="help-note">
            * 녹색 숫자 = 절감 (좋은 것) / 빨간색 숫자 = 증가 (안 좋은 것)
          </div>
        </div>
      </div>

      <div class="info-section">
        <div class="info-row">
          <div class="info-item">
            <span class="info-label">조회 연도</span>
            <select v-model="selectedYear" class="year-select" @change="fetchYearlyData">
              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}년</option>
            </select>
          </div>
          <div class="info-item">
            <span class="info-label">데이터 현황</span>
            <span class="info-value">
              <span class="data-badge ledger">임금대장: {{ dataSourceCount.ledger }}개월</span>
              <span class="data-badge excel">엑셀업로드: {{ dataSourceCount.excel }}개월</span>
              <span class="data-badge none">데이터없음: {{ dataSourceCount.none }}개월</span>
            </span>
          </div>
        </div>
      </div>

      <div class="action-section">
        <v-btn class="btnColorDark" @click="generatePdf" :disabled="!hasAnyData" :loading="isGeneratingPdf">
          PDF 생성
        </v-btn>
      </div>

      <div class="modal-content" v-if="loading">
        <div class="loading-state">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p>데이터를 불러오는 중...</p>
        </div>
      </div>

      <div class="modal-content" v-else-if="hasAnyData">
        <v-tabs v-model="activeTab" color="primary">
          <v-tab value="monthly">월별 상세</v-tab>
          <v-tab value="summary">연간 요약</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <v-window-item value="monthly">
            <div class="unit-label">단위: 원</div>
            <div class="monthly-table-wrapper">
              <table class="monthly-table">
                <thead>
                  <tr>
                    <th class="sticky-col">월</th>
                    <th>데이터 출처</th>
                    <th>대상 인원</th>
                    <th>총급여</th>
                    <th class="before-header">개선전 비과세</th>
                    <th class="before-header">개선전 기관부담</th>
                    <th class="before-header">개선전 퇴직금</th>
                    <th class="after-header">개선후 비과세</th>
                    <th class="after-header">개선후 기관부담</th>
                    <th class="after-header">개선후 퇴직금</th>
                    <th class="diff-header">기관부담 차액</th>
                    <th class="diff-header">퇴직금 차액</th>
                    <th class="saving-header">월 절감액</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="monthData in monthlySummary" :key="monthData.month" 
                      :class="{ 'no-data-row': !monthData.hasData }">
                    <td class="sticky-col">{{ formatMonth(monthData.month) }}</td>
                    <td class="text-center">
                      <span v-if="monthData.dataSource === 'ledger'" class="source-badge ledger">임금대장</span>
                      <span v-else-if="monthData.dataSource === 'excel'" class="source-badge excel">엑셀업로드</span>
                      <span v-else class="source-badge none">-</span>
                    </td>
                    <td class="text-center">{{ monthData.hasData ? monthData.employeeCount + '명' : '-' }}</td>
                    <td class="text-right">{{ monthData.hasData ? monthData.totalPay : '-' }}</td>
                    <td class="before-cell text-right">{{ monthData.hasData ? monthData.before.nonTax : '-' }}</td>
                    <td class="before-cell text-right">{{ monthData.hasData ? monthData.before.companyIns : '-' }}</td>
                    <td class="before-cell text-right">{{ monthData.hasData ? monthData.before.retirement : '-' }}</td>
                    <td class="after-cell text-right">{{ monthData.hasData ? monthData.after.nonTax : '-' }}</td>
                    <td class="after-cell text-right">{{ monthData.hasData ? monthData.after.companyIns : '-' }}</td>
                    <td class="after-cell text-right">{{ monthData.hasData ? monthData.after.retirement : '-' }}</td>
                    <td class="diff-cell text-right" :class="getDiffClass(monthData.diff?.companyIns, true)">
                      {{ monthData.hasData ? monthData.diff.companyIns : '-' }}
                    </td>
                    <td class="diff-cell text-right" :class="getDiffClass(monthData.diff?.retirement, true)">
                      {{ monthData.hasData ? monthData.diff.retirement : '-' }}
                    </td>
                    <td class="saving-cell text-right font-bold" :class="getDiffClass(monthData.saving, true)">
                      {{ monthData.hasData ? monthData.saving : '-' }}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="total-row">
                    <td class="sticky-col font-bold">연간 합계</td>
                    <td class="text-center font-bold">{{ dataSourceCount.ledger + dataSourceCount.excel }}개월</td>
                    <td class="text-center font-bold">-</td>
                    <td class="text-right font-bold">{{ yearlyTotals.totalPay }}</td>
                    <td class="before-cell text-right font-bold">{{ yearlyTotals.before.nonTax }}</td>
                    <td class="before-cell text-right font-bold">{{ yearlyTotals.before.companyIns }}</td>
                    <td class="before-cell text-right font-bold">{{ yearlyTotals.before.retirement }}</td>
                    <td class="after-cell text-right font-bold">{{ yearlyTotals.after.nonTax }}</td>
                    <td class="after-cell text-right font-bold">{{ yearlyTotals.after.companyIns }}</td>
                    <td class="after-cell text-right font-bold">{{ yearlyTotals.after.retirement }}</td>
                    <td class="diff-cell text-right font-bold" :class="getDiffClass(yearlyTotals.diff.companyIns, true)">
                      {{ yearlyTotals.diff.companyIns }}
                    </td>
                    <td class="diff-cell text-right font-bold" :class="getDiffClass(yearlyTotals.diff.retirement, true)">
                      {{ yearlyTotals.diff.retirement }}
                    </td>
                    <td class="saving-cell text-right font-bold" :class="getDiffClass(yearlyTotals.saving, true)">
                      {{ yearlyTotals.saving }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </v-window-item>

          <v-window-item value="summary">
            <div class="summary-section">
              <div class="summary-cards">
                <div class="summary-card before-card">
                  <h3>개선전 (생산직비과세 미적용)</h3>
                  <div class="summary-row">
                    <span class="label">연간 총급여</span>
                    <span class="value">{{ yearlyTotals.totalPay }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">연간 비과세</span>
                    <span class="value">{{ yearlyTotals.before.nonTax }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">연간 기관부담 4대보험</span>
                    <span class="value">{{ yearlyTotals.before.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">연간 퇴직금</span>
                    <span class="value">{{ yearlyTotals.before.retirement }}</span>
                  </div>
                </div>

                <div class="summary-card after-card">
                  <h3>개선후 (생산직비과세 적용)</h3>
                  <div class="summary-row">
                    <span class="label">연간 총급여</span>
                    <span class="value">{{ yearlyTotals.totalPay }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">연간 비과세</span>
                    <span class="value">{{ yearlyTotals.after.nonTax }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">연간 기관부담 4대보험</span>
                    <span class="value">{{ yearlyTotals.after.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">연간 퇴직금</span>
                    <span class="value">{{ yearlyTotals.after.retirement }}</span>
                  </div>
                </div>

                <div class="summary-card diff-card">
                  <h3>연간 기관 비용 절감 효과</h3>
                  <div class="summary-row highlight-row">
                    <span class="label">데이터 보유 기간</span>
                    <span class="value">{{ dataSourceCount.ledger + dataSourceCount.excel }}개월</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">연간 기관부담 차액</span>
                    <span class="value" :class="getDiffClass(yearlyTotals.diff.companyIns, true)">
                      {{ yearlyTotals.diff.companyIns }}
                    </span>
                  </div>
                  <div class="summary-row">
                    <span class="label">연간 퇴직금 차액</span>
                    <span class="value" :class="getDiffClass(yearlyTotals.diff.retirement, true)">
                      {{ yearlyTotals.diff.retirement }}
                    </span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row total">
                    <span class="label">연간 총 절감액</span>
                    <span class="value large" :class="getDiffClass(yearlyTotals.saving, true)">
                      {{ yearlyTotals.saving }}
                    </span>
                  </div>
                  <div class="summary-row info">
                    <span class="label">월 평균 절감액</span>
                    <span class="value" :class="getDiffClass(yearlyTotals.saving, true)">
                      {{ yearlyTotals.avgSaving }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="monthly-chart-section">
                <h3>월별 절감액 추이</h3>
                <div class="chart-container">
                  <div v-for="monthData in monthlySummary" :key="monthData.month" class="chart-bar-wrapper">
                    <div class="chart-bar" 
                         :style="{ height: getBarHeight(monthData.savingRaw) + 'px' }"
                         :class="{ positive: monthData.savingRaw < 0, negative: monthData.savingRaw > 0, 'no-data': !monthData.hasData }">
                    </div>
                    <span class="chart-label">{{ monthData.month.split('-')[1] }}월</span>
                    <span class="chart-value" v-if="monthData.hasData">{{ monthData.saving }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>
        </v-window>
      </div>

      <div class="modal-content empty-state" v-else>
        <p>{{ selectedYear }}년 데이터가 없습니다.</p>
      </div>

      <div class="modal-footer">
        <v-btn class="cancelRegistBtn" @click="closeDialog">닫기</v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from "vue";
import { useStore } from "vuex";
import axios from "axios";

interface MonthlyData {
  month: string;
  hasData: boolean;
  dataSource: string | null;
  employeeCount: number;
  totalPay: string;
  before: { nonTax: string; companyIns: string; retirement: string };
  after: { nonTax: string; companyIns: string; retirement: string };
  diff: { nonTax: string; companyIns: string; retirement: string };
  saving: string;
  savingRaw: number;
}

const store = useStore();
const dialog = ref(false);
const loading = ref(false);
const activeTab = ref("monthly");
const selectedYear = ref("");
const isGeneratingPdf = ref(false);
const showHelp = ref(false);
const monthlySummary = ref<MonthlyData[]>([]);
const yearlyRawData = ref<any[]>([]);

const availableYears = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(String(y));
  }
  return years;
});

const dataSourceCount = computed(() => {
  let ledger = 0;
  let excel = 0;
  let none = 0;
  
  for (const month of monthlySummary.value) {
    if (month.dataSource === 'ledger') ledger++;
    else if (month.dataSource === 'excel') excel++;
    else none++;
  }
  
  return { ledger, excel, none };
});

const hasAnyData = computed(() => {
  return dataSourceCount.value.ledger > 0 || dataSourceCount.value.excel > 0;
});

const yearlyTotals = reactive({
  totalPay: "0",
  before: { nonTax: "0", companyIns: "0", retirement: "0" },
  after: { nonTax: "0", companyIns: "0", retirement: "0" },
  diff: { nonTax: "0", companyIns: "0", retirement: "0" },
  saving: "0",
  avgSaving: "0"
});

const nc = (value: any): number => {
  if (value == null) return 0;
  return parseFloat(String(value).replace(/,/g, "")) || 0;
};

const ac = (number: number): string => {
  if (number === 0) return "0";
  return Math.round(number).toLocaleString();
};

const formatMonth = (ym: string): string => {
  if (!ym) return "";
  const parts = ym.split("-");
  if (parts.length === 2) {
    return `${parseInt(parts[1], 10)}월`;
  }
  return ym;
};

const getDiffClass = (value: string | undefined, isReverse = false): string => {
  if (!value) return "";
  const num = nc(value);
  if (isReverse) {
    if (num < 0) return "positive-diff";
    if (num > 0) return "negative-diff";
  } else {
    if (num > 0) return "positive-diff";
    if (num < 0) return "negative-diff";
  }
  return "";
};

const getBarHeight = (saving: number): number => {
  if (!saving) return 0;
  const absSaving = Math.abs(saving);
  const maxSaving = Math.max(...monthlySummary.value.map(m => Math.abs(m.savingRaw || 0)));
  if (maxSaving === 0) return 0;
  return Math.min(100, (absSaving / maxSaving) * 100);
};

const openDialog = async () => {
  dialog.value = true;
  activeTab.value = "monthly";
  selectedYear.value = store.state.AdminYYMM?.split('-')[0] || String(new Date().getFullYear());
  await fetchYearlyData();
};

const closeDialog = () => {
  dialog.value = false;
  showHelp.value = false;
};

const fetchYearlyData = async () => {
  loading.value = true;
  monthlySummary.value = [];
  yearlyRawData.value = [];
  
  try {
    const code = store.state.AdminSisulCode;
    const bizGubun = store.state.AdminBizGubun;
    
    const response = await axios.get(`/api/getYearlyComparisonData/${code}/${selectedYear.value}/${bizGubun}`);
    
    if (response.data.success) {
      yearlyRawData.value = response.data.data;
      const tax = response.data.tax;
      
      calculateYearlyComparison(response.data.data, tax);
    }
  } catch (error) {
    console.error("연간 데이터 조회 실패:", error);
    store.commit("showSnackbar", { text: "연간 데이터 조회 중 오류가 발생했습니다.", color: "error" });
  } finally {
    loading.value = false;
  }
};

const calculateYearlyComparison = (yearData: any[], tax: any) => {
  const results: MonthlyData[] = [];
  
  let sumTotalPay = 0;
  let sumBefore = { nonTax: 0, companyIns: 0, retirement: 0 };
  let sumAfter = { nonTax: 0, companyIns: 0, retirement: 0 };
  
  for (const monthItem of yearData) {
    if (!monthItem.hasData || !monthItem.data) {
      results.push({
        month: monthItem.month,
        hasData: false,
        dataSource: null,
        employeeCount: 0,
        totalPay: "-",
        before: { nonTax: "-", companyIns: "-", retirement: "-" },
        after: { nonTax: "-", companyIns: "-", retirement: "-" },
        diff: { nonTax: "-", companyIns: "-", retirement: "-" },
        saving: "-",
        savingRaw: 0
      });
      continue;
    }
    
    let monthTotalPay = 0;
    let monthBefore = { nonTax: 0, companyIns: 0, retirement: 0 };
    let monthAfter = { nonTax: 0, companyIns: 0, retirement: 0 };
    let employeeCount = 0;
    
    if (monthItem.dataSource === 'ledger') {
      const { wageData, basicData } = monthItem.data;
      employeeCount = wageData.length;
      
      for (const wage of wageData) {
        const basic = basicData.find((b: any) => b.empNum === wage.empNum);
        const total = nc(wage.total);
        monthTotalPay += total;
        
        const afterTaxable = nc(wage.taxable);
        const afterNonTax = total - afterTaxable;
        monthAfter.nonTax += afterNonTax;
        
        const afterCompanyIns = nc(wage.c_yeon) + nc(wage.c_gun) + nc(wage.c_jang) + 
                               nc(wage.c_go) + nc(wage.c_sil) + nc(wage.c_san);
        monthAfter.companyIns += afterCompanyIns;
        monthAfter.retirement += nc(wage.c_out);
        
        if (basic) {
          const beforeLunch = nc(basic.eaLunchPay);
          const beforeNonTax = nc(basic.eaDangPay) + beforeLunch + nc(basic.eaCarPay) + nc(basic.eaChildcarePay);
          const roundedBeforeNonTax = Math.floor(beforeNonTax / 10) * 10;
          monthBefore.nonTax += roundedBeforeNonTax;
          
          const beforeTaxable = Math.floor((total - roundedBeforeNonTax) / 10) * 10;
          
          const before_c_gun = nc(wage.c_gun) === 0 ? 0 : 
            Math.max(nc(wage.c_gun), Math.floor((beforeTaxable * (tax.tr_gun || 0.03545)) / 10) * 10);
          const before_c_jang = nc(wage.c_jang) === 0 ? 0 :
            Math.floor((before_c_gun * (tax.tr_jang || 0.1295)) / 10) * 10;
          const before_c_go = nc(wage.c_go) === 0 ? 0 :
            Math.max(nc(wage.c_go), Math.floor((beforeTaxable * (tax.tr_go || 0.009)) / 10) * 10);
          const before_c_sil = nc(wage.c_sil) === 0 ? 0 :
            Math.max(nc(wage.c_sil), Math.floor((beforeTaxable * (tax.tr_sil || 0.0055)) / 10) * 10);
          
          monthBefore.companyIns += nc(wage.c_yeon) + before_c_gun + before_c_jang + before_c_go + before_c_sil + nc(wage.c_san);
          monthBefore.retirement += nc(wage.c_out) === 0 ? 0 : Math.floor(((total - beforeLunch) / 12) / 10) * 10;
        }
      }
    } else if (monthItem.dataSource === 'excel') {
      const { excelData, calcData } = monthItem.data;
      employeeCount = calcData.length;
      
      for (const calc of calcData) {
        const excel = excelData.find((e: any) => e.empName === calc.empName);
        const total = nc(calc.total);
        monthTotalPay += total;
        
        const afterTaxable = nc(calc.taxable);
        const afterNonTax = total - afterTaxable;
        monthAfter.nonTax += afterNonTax;
        
        const afterCompanyIns = nc(calc.c_yeon) + nc(calc.c_gun) + nc(calc.c_jang) + 
                               nc(calc.c_go) + nc(calc.c_sil) + nc(calc.c_san);
        monthAfter.companyIns += afterCompanyIns;
        monthAfter.retirement += nc(calc.c_out);
        
        if (excel) {
          const beforeLunch = nc(excel.lunch);
          const beforeNonTax = nc(excel.dang) + beforeLunch + nc(excel.car) + nc(excel.childcare);
          const roundedBeforeNonTax = Math.floor(beforeNonTax / 10) * 10;
          monthBefore.nonTax += roundedBeforeNonTax;
          
          const beforeTaxable = Math.floor((total - roundedBeforeNonTax) / 10) * 10;
          
          let before_c_gun = 0, before_c_jang = 0, before_c_go = 0, before_c_sil = 0;
          if (afterTaxable > 0) {
            before_c_gun = nc(calc.c_gun) === 0 ? 0 :
              Math.floor((beforeTaxable * (nc(calc.c_gun) / afterTaxable)) / 10) * 10;
            before_c_jang = (nc(calc.c_jang) === 0 || nc(calc.c_gun) === 0) ? 0 :
              Math.floor((before_c_gun * (nc(calc.c_jang) / nc(calc.c_gun))) / 10) * 10;
            before_c_go = nc(calc.c_go) === 0 ? 0 :
              Math.floor((beforeTaxable * (nc(calc.c_go) / afterTaxable)) / 10) * 10;
            before_c_sil = nc(calc.c_sil) === 0 ? 0 :
              Math.floor((beforeTaxable * (nc(calc.c_sil) / afterTaxable)) / 10) * 10;
          }
          monthBefore.companyIns += nc(calc.c_yeon) + before_c_gun + before_c_jang + before_c_go + before_c_sil + nc(calc.c_san);
          monthBefore.retirement += nc(calc.c_out) === 0 ? 0 : Math.floor(((total - beforeLunch) / 12) / 10) * 10;
        }
      }
    }
    
    const diffCompanyIns = monthAfter.companyIns - monthBefore.companyIns;
    const diffRetirement = monthAfter.retirement - monthBefore.retirement;
    const saving = diffCompanyIns + diffRetirement;
    
    sumTotalPay += monthTotalPay;
    sumBefore.nonTax += monthBefore.nonTax;
    sumBefore.companyIns += monthBefore.companyIns;
    sumBefore.retirement += monthBefore.retirement;
    sumAfter.nonTax += monthAfter.nonTax;
    sumAfter.companyIns += monthAfter.companyIns;
    sumAfter.retirement += monthAfter.retirement;
    
    results.push({
      month: monthItem.month,
      hasData: true,
      dataSource: monthItem.dataSource,
      employeeCount: employeeCount,
      totalPay: ac(monthTotalPay),
      before: {
        nonTax: ac(monthBefore.nonTax),
        companyIns: ac(monthBefore.companyIns),
        retirement: ac(monthBefore.retirement)
      },
      after: {
        nonTax: ac(monthAfter.nonTax),
        companyIns: ac(monthAfter.companyIns),
        retirement: ac(monthAfter.retirement)
      },
      diff: {
        nonTax: ac(monthAfter.nonTax - monthBefore.nonTax),
        companyIns: ac(diffCompanyIns),
        retirement: ac(diffRetirement)
      },
      saving: ac(saving),
      savingRaw: saving
    });
  }
  
  monthlySummary.value = results;
  
  const diffNonTax = sumAfter.nonTax - sumBefore.nonTax;
  const diffCompanyIns = sumAfter.companyIns - sumBefore.companyIns;
  const diffRetirement = sumAfter.retirement - sumBefore.retirement;
  const totalSaving = diffCompanyIns + diffRetirement;
  const monthCount = dataSourceCount.value.ledger + dataSourceCount.value.excel;
  
  yearlyTotals.totalPay = ac(sumTotalPay);
  yearlyTotals.before.nonTax = ac(sumBefore.nonTax);
  yearlyTotals.before.companyIns = ac(sumBefore.companyIns);
  yearlyTotals.before.retirement = ac(sumBefore.retirement);
  yearlyTotals.after.nonTax = ac(sumAfter.nonTax);
  yearlyTotals.after.companyIns = ac(sumAfter.companyIns);
  yearlyTotals.after.retirement = ac(sumAfter.retirement);
  yearlyTotals.diff.nonTax = ac(diffNonTax);
  yearlyTotals.diff.companyIns = ac(diffCompanyIns);
  yearlyTotals.diff.retirement = ac(diffRetirement);
  yearlyTotals.saving = ac(totalSaving);
  yearlyTotals.avgSaving = monthCount > 0 ? ac(Math.round(totalSaving / monthCount)) : "0";
};

const generatePdf = async () => {
  isGeneratingPdf.value = true;
  try {
    const response = await axios.post("/api/getYearlyBeforeAfterPdf", {
      monthlyData: monthlySummary.value,
      yearlyTotals: {
        totalPay: yearlyTotals.totalPay,
        before: yearlyTotals.before,
        after: yearlyTotals.after,
        diff: yearlyTotals.diff,
        saving: yearlyTotals.saving,
        avgSaving: yearlyTotals.avgSaving
      },
      sisulName: store.state.AdminSisulName,
      year: selectedYear.value
    });

    const pdfBase64 = response.data.pdf;
    const binaryString = window.atob(pdfBase64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const newWindow = window.open(url, "_blank");
    if (newWindow) {
      newWindow.focus();
    }
  } catch (error) {
    console.error("PDF 생성 실패:", error);
    store.commit("showSnackbar", { text: "PDF 생성 중 오류가 발생했습니다.", color: "error" });
  } finally {
    isGeneratingPdf.value = false;
  }
};

defineExpose({ openDialog });
</script>

<style scoped>
.yearlyModalCard {
  background-color: #fff;
  padding: 24px;
  border-radius: 10px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-header h2 {
  margin: 0;
  font-weight: 600;
}

.help-icon {
  cursor: pointer;
  transition: color 0.2s;
}

.help-icon:hover {
  color: #1565c0 !important;
}

.close-btn {
  width: 35px;
  height: 35px;
  cursor: pointer;
}

.help-section {
  background: linear-gradient(135deg, #f8f9ff 0%, #fff8f0 100%);
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 16px;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.help-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.help-title {
  font-size: 16px;
  font-weight: 600;
  color: #1565c0;
}

.help-close {
  cursor: pointer;
}

.help-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.help-block {
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #eee;
}

.help-subtitle {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  padding-left: 8px;
  border-left: 3px solid #1565c0;
}

.help-highlight {
  font-size: 14px;
  line-height: 1.8;
  color: #444;
  margin: 0;
}

.help-comparison {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.help-compare-box {
  flex: 1;
  min-width: 180px;
  max-width: 250px;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
}

.help-compare-box.before {
  background-color: #fff3e0;
  border: 1px solid #ffcc80;
}

.help-compare-box.before strong {
  color: #e65100;
}

.help-compare-box.after {
  background-color: #e8f5e9;
  border: 1px solid #a5d6a7;
}

.help-compare-box.after strong {
  color: #2e7d32;
}

.help-compare-box p {
  margin: 8px 0 0 0;
  font-size: 13px;
  color: #666;
}

.help-arrow {
  font-size: 24px;
  color: #999;
}

.help-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.help-table tr {
  border-bottom: 1px solid #eee;
}

.help-table tr:last-child {
  border-bottom: none;
}

.help-table td {
  padding: 10px 8px;
  vertical-align: top;
}

.help-term {
  width: 130px;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
}

.help-example {
  background-color: #fafafa;
  padding: 16px;
  border-radius: 6px;
  font-size: 13px;
}

.help-example p {
  margin: 0 0 12px 0;
}

.help-calc-row {
  display: flex;
  gap: 8px;
  padding: 4px 0;
}

.help-calc-row.mt {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #ddd;
}

.help-calc-row.result {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ccc;
  background-color: #e8f5e9;
  margin: 12px -16px -16px -16px;
  padding: 12px 16px;
  border-radius: 0 0 6px 6px;
}

.calc-label {
  width: 120px;
  color: #666;
  flex-shrink: 0;
}

.text-green {
  color: #2e7d32;
  font-weight: 500;
}

.text-red {
  color: #d32f2f;
  font-weight: 500;
}

.help-note {
  font-size: 12px;
  color: #888;
  text-align: center;
  padding-top: 8px;
}

.info-section {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-weight: 500;
  color: #666;
  font-size: 14px;
}

.info-value {
  display: flex;
  gap: 8px;
}

.year-select {
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  background-color: #fff;
}

.data-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.data-badge.ledger {
  background-color: #e3f2fd;
  color: #1565c0;
}

.data-badge.excel {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.data-badge.none {
  background-color: #f5f5f5;
  color: #999;
}

.action-section {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.modal-content {
  min-height: 300px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
  color: #666;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.unit-label {
  text-align: right;
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  padding-right: 10px;
}

.monthly-table-wrapper {
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;
  margin-top: 16px;
}

.monthly-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.monthly-table th,
.monthly-table td {
  border: 1px solid #ddd;
  padding: 8px 12px;
  white-space: nowrap;
}

.monthly-table thead th {
  background-color: #f5f5f5;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sticky-col {
  position: sticky;
  left: 0;
  background-color: #fff;
  z-index: 5;
  font-weight: 500;
}

.monthly-table thead .sticky-col {
  z-index: 15;
  background-color: #f5f5f5;
}

.before-header {
  background-color: #fff3e0 !important;
  color: #e65100;
}

.after-header {
  background-color: #e8f5e9 !important;
  color: #2e7d32;
}

.diff-header {
  background-color: #e3f2fd !important;
  color: #1565c0;
}

.saving-header {
  background-color: #fce4ec !important;
  color: #c2185b;
}

.before-cell {
  background-color: #fffaf5;
}

.after-cell {
  background-color: #f5fff5;
}

.diff-cell {
  background-color: #f5f9ff;
}

.saving-cell {
  background-color: #fff5f7;
}

.no-data-row {
  background-color: #fafafa;
  color: #999;
}

.no-data-row .sticky-col {
  background-color: #fafafa;
}

.source-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
}

.source-badge.ledger {
  background-color: #e3f2fd;
  color: #1565c0;
}

.source-badge.excel {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.source-badge.none {
  color: #999;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.font-bold {
  font-weight: 700;
}

.total-row td {
  background-color: #f0f0f0 !important;
  font-weight: 600;
}

.total-row .sticky-col {
  background-color: #f0f0f0 !important;
}

.positive-diff {
  color: #2e7d32;
}

.negative-diff {
  color: #d32f2f;
}

.summary-section {
  padding: 20px 0;
}

.summary-cards {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 30px;
}

.summary-card {
  flex: 1;
  min-width: 300px;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #ddd;
}

.summary-card h3 {
  margin: 0 0 16px 0;
  font-size: 15px;
  font-weight: 600;
}

.before-card {
  background-color: #fff8f0;
  border-color: #ffcc80;
}

.before-card h3 {
  color: #e65100;
}

.after-card {
  background-color: #f0fff0;
  border-color: #a5d6a7;
}

.after-card h3 {
  color: #2e7d32;
}

.diff-card {
  background-color: #f0f7ff;
  border-color: #90caf9;
}

.diff-card h3 {
  color: #1565c0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
}

.summary-row .label {
  color: #666;
}

.summary-row .value {
  font-weight: 500;
}

.summary-row.total {
  border-top: 1px solid #ddd;
  margin-top: 8px;
  padding-top: 12px;
  font-weight: 600;
}

.summary-row.total .label,
.summary-row.total .value {
  font-weight: 600;
}

.summary-row.highlight-row {
  background-color: #e3f2fd;
  margin: -8px -14px 0 -14px;
  padding: 10px 14px;
  border-radius: 4px;
}

.summary-row.highlight-row .value {
  font-weight: 600;
  color: #1565c0;
}

.divider {
  height: 1px;
  background-color: #ddd;
  margin: 12px 0;
}

.large {
  font-size: 18px;
}

.monthly-chart-section {
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 10px;
}

.monthly-chart-section h3 {
  margin: 0 0 20px 0;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.chart-container {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  height: 150px;
  padding: 0 10px;
  gap: 8px;
}

.chart-bar-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.chart-bar {
  width: 100%;
  max-width: 40px;
  border-radius: 4px 4px 0 0;
  transition: height 0.3s ease;
}

.chart-bar.positive {
  background-color: #4caf50;
}

.chart-bar.negative {
  background-color: #f44336;
}

.chart-bar.no-data {
  background-color: #e0e0e0;
  height: 4px !important;
}

.chart-label {
  margin-top: 8px;
  font-size: 11px;
  color: #666;
}

.chart-value {
  font-size: 10px;
  color: #333;
  margin-top: 2px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  margin-top: 20px;
}
</style>
